import { useEffect } from "react";

const DEFAULTS = {
  siteName: "L'Entracte",
  defaultImage: "https://customer-assets.emergentagent.com/job_entracte-backoffice/artifacts/6xlo6wjr_IMG_6691-e1690204737113-1000x720.webp",
};

/** Production site origin. All canonical URLs are absolute against this. */
const CANONICAL_ORIGIN = "https://lentracterestaurant.fr";

/**
 * Build a canonical URL from a pathname.
 * Rules:
 *  - Absolute against the production origin (never uses window.location.origin,
 *    so preview URLs never leak into canonicals).
 *  - No query string, no hash. UTM / fbclid / gclid / any tracking or form
 *    parameters MUST NOT appear in canonical or og:url.
 *  - No trailing slash except for the root ("/").
 */
export function buildCanonical(pathname) {
  let p = (pathname || "/").split("?")[0].split("#")[0];
  if (!p.startsWith("/")) p = "/" + p;
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return `${CANONICAL_ORIGIN}${p}`;
}

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, attrs = {}) {
  if (!href) return;
  const selector = Object.entries(attrs).reduce(
    (s, [k, v]) => `${s}[${k}="${v}"]`,
    `link[rel="${rel}"]`
  );
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeLinks(rel) {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((n) => n.remove());
}

function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Manage <head> tags (title, meta, OG, canonical, hreflang, GEO, JSON-LD).
 *
 * Options:
 *  - title, description, image, keywords, type
 *  - lang: "fr" | "en"  → sets og:locale + og:locale:alternate
 *  - alternates: { fr, en, xDefault }  → hreflang links
 *  - jsonLd: object or array
 */
export function useSEO({
  title,
  description,
  image,
  keywords,
  canonical,
  jsonLd,
  type = "website",
  lang = "fr",
  alternates,
  noindex = false,
} = {}) {
  useEffect(() => {
    const isEn = lang === "en";
    const fullTitle = title
      ? `${title} · ${DEFAULTS.siteName}`
      : isEn
      ? `${DEFAULTS.siteName} · French Restaurant · Ville d'Avray`
      : `${DEFAULTS.siteName} · Restaurant & Réception · Ville d'Avray`;
    document.title = fullTitle;

    const fallbackDesc = isEn
      ? "L'Entracte, refined French restaurant in Ville d'Avray. House-made seasonal cuisine, 240 m² terrace, private receptions up to 100 guests."
      : "L'Entracte, restaurant français raffiné à Ville d'Avray. Cuisine maison de saison, terrasse de 240m², réceptions privées jusqu'à 100 convives.";
    const desc = description || fallbackDesc;
    const img = image || DEFAULTS.defaultImage;
    // Canonical: always absolute against the production origin,
    // stripped of query params and hash. Never uses window.location.href.
    const url = canonical || (typeof window !== "undefined"
      ? buildCanonical(window.location.pathname)
      : `${CANONICAL_ORIGIN}/`);

    upsertMeta("name", "description", desc);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    upsertMeta("name", "author", DEFAULTS.siteName);
    upsertMeta("http-equiv", "content-language", isEn ? "en-GB" : "fr-FR");

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:alt", DEFAULTS.siteName);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", DEFAULTS.siteName);
    upsertMeta("property", "og:locale", isEn ? "en_GB" : "fr_FR");
    upsertMeta("property", "og:locale:alternate", isEn ? "fr_FR" : "en_GB");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", img);

    // GEO meta tags (Ville d'Avray, FR)
    upsertMeta("name", "geo.region", "FR-92");
    upsertMeta("name", "geo.placename", "Ville d'Avray");
    upsertMeta("name", "geo.position", "48.8263450;2.1882610");
    upsertMeta("name", "ICBM", "48.8263450, 2.1882610");

    // Canonical
    upsertLink("canonical", url);

    // hreflang alternates
    removeLinks("alternate");
    if (alternates) {
      if (alternates.fr) upsertLink("alternate", alternates.fr, { hreflang: "fr-FR" });
      if (alternates.fr) upsertLink("alternate", alternates.fr, { hreflang: "fr" });
      if (alternates.en) upsertLink("alternate", alternates.en, { hreflang: "en-GB" });
      if (alternates.en) upsertLink("alternate", alternates.en, { hreflang: "en" });
      if (alternates.xDefault) upsertLink("alternate", alternates.xDefault, { hreflang: "x-default" });
    }
    // RSS / Atom (none for now)

    // Only manage runtime JSON-LD scripts (ids starting with "ld-runtime-").
    // Static schemas injected by index.html use "ld-global-*" and MUST be
    // preserved so raw HTML consumed by non-JS crawlers keeps its schema.
    if (jsonLd) {
      const list = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      document.querySelectorAll('script[id^="ld-runtime-"]').forEach((s) => s.remove());
      list.forEach((d, i) => upsertJsonLd(`ld-runtime-${i}`, d));
    } else {
      document.querySelectorAll('script[id^="ld-runtime-"]').forEach((s) => s.remove());
    }
  }, [title, description, image, keywords, canonical, type, lang, noindex, JSON.stringify(jsonLd), JSON.stringify(alternates)]);
}

const SITE_URL = "https://lentracterestaurant.fr";

/** Rich Restaurant + EventVenue (Schema.org) — global entity for all pages. */
export const RESTAURANT_LD = {
  "@context": "https://schema.org",
  "@type": ["Restaurant", "EventVenue"],
  "@id": `${SITE_URL}/#lieu`,
  "name": "L'Entracte",
  "alternateName": "L'Entracte Restaurant & Réception",
  "description":
    "Restaurant français et lieu de réception dans un hôtel particulier à Ville-d'Avray : cuisine maison de saison, terrasse de 240 m², privatisation de 12 à 100 convives pour mariages, séminaires, baptêmes et anniversaires.",
  "url": `${SITE_URL}/`,
  "logo": `${SITE_URL}/logo.webp`,
  "image": [
    "https://customer-assets.emergentagent.com/job_entracte-backoffice/artifacts/6xlo6wjr_IMG_6691-e1690204737113-1000x720.webp",
    "https://customer-assets.emergentagent.com/job_entracte-backoffice/artifacts/z4wi3c5x_IMG_4310.webp",
    "https://customer-assets.emergentagent.com/job_entracte-backoffice/artifacts/9dtrxni8_IMG_6870-1.webp",
  ],
  "telephone": "+33-1-82-00-18-67",
  "email": "restaurantlentractevda@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "3 rue de Versailles",
    "postalCode": "92410",
    "addressLocality": "Ville-d'Avray",
    "addressRegion": "Île-de-France",
    "addressCountry": "FR",
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 48.826345,
    "longitude": 2.188261,
  },
  "hasMap": "https://maps.app.goo.gl/wM224MLpjMTpzh7z8",
  "servesCuisine": ["Cuisine française", "Cuisine de saison"],
  "priceRange": "€€",
  "acceptsReservations": `${SITE_URL}/reservation`,
  "hasMenu": `${SITE_URL}/menu`,
  "maximumAttendeeCapacity": 100,
  "openingHoursSpecification": [
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "11:30", "closes": "14:30" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "19:30", "closes": "22:00" },
    { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "11:30", "closes": "14:30" },
  ],
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Terrasse de 240 m² sur parc", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Privatisation totale ou partielle", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Parking", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Wi-Fi", "value": true },
  ],
  "sameAs": [
    "https://www.instagram.com/lentracte_villedavray/",
    "https://www.facebook.com/LentracteVilledavray/",
    "https://www.tripadvisor.fr/Restaurant_Review-g608780-d23720849-Reviews-L_Entracte-Ville_D_Avray_Hauts_de_Seine_Ile_de_France.html",
    "https://www.mariages.net/restaurant-mariage/l-entracte-restaurant-&-reception--e278258",
    "https://www.1001salles.com/mariage/46596-l-entracte-restaurant.aspx",
    "https://www.kactus.com/fr/lieux/l-entracte-restaurant-reception",
    "https://bridebook.com/fr/wedding-venues/l-entracte-restaurant-r-ception-ville-d-avray-hauts-de-seine-fro8aOLB0g",
  ],
};

/** Organization (for sitelinks brand box) */
export const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  "name": "L'Entracte",
  "url": `${SITE_URL}/`,
  "logo": `${SITE_URL}/logo.webp`,
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+33-1-82-00-18-67",
    "email": "restaurantlentractevda@gmail.com",
    "contactType": "reservations",
    "areaServed": "FR",
    "availableLanguage": ["French", "English"],
  },
};

/** Build a Menu / MenuItem Schema.org graph from API categories + dishes. */
export function buildMenuLD(categories = [], dishes = []) {
  const sections = categories
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((cat) => {
      const items = dishes
        .filter((d) => d.category_id === cat.id && d.available !== false)
        .map((d) => ({
          "@type": "MenuItem",
          "@id": `${SITE_URL}/menu#dish-${d.id}`,
          "name": d.name,
          "description": d.description || undefined,
          "offers": {
            "@type": "Offer",
            "price": d.price?.toFixed?.(2) ?? d.price,
            "priceCurrency": "EUR",
          },
          "suitableForDiet": d.is_vegetarian ? ["https://schema.org/VegetarianDiet"] : undefined,
        }));
      return {
        "@type": "MenuSection",
        "name": cat.name,
        "hasMenuItem": items,
      };
    });
  return {
    "@context": "https://schema.org",
    "@type": "Menu",
    "@id": `${SITE_URL}/menu#menu`,
    "name": "Carte L'Entracte",
    "inLanguage": "fr-FR",
    "hasMenuSection": sections,
  };
}

export const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": it.name,
    "item": it.url,
  })),
});

/** FAQPage for AI/search visibility */
export const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Quels sont les horaires d'ouverture de L'Entracte ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "L'Entracte est ouvert du mardi au samedi de 11h30 à 14h30 et de 19h30 à 22h, ainsi que le dimanche de 11h30 à 14h30. Fermé le lundi.",
      },
    },
    {
      "@type": "Question",
      "name": "Faut-il réserver à L'Entracte ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, la réservation est fortement recommandée. Vous pouvez réserver en ligne via Zenchef sur la page /reservation ou par téléphone au 01 82 00 18 67.",
      },
    },
    {
      "@type": "Question",
      "name": "L'Entracte propose-t-il un menu midi ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, un Menu Midi à 33€ est proposé du mardi au vendredi (hors jours fériés) : Entrée + Plat ou Plat + Dessert au choix parmi les plats annotés d'une étoile sur la carte.",
      },
    },
    {
      "@type": "Question",
      "name": "Peut-on privatiser L'Entracte pour un événement ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, nous privatisons totalement ou partiellement l'établissement pour des événements de 12 à 100 convives : mariages, anniversaires, baptêmes, séminaires. Demandez un devis sur la page Événements.",
      },
    },
    {
      "@type": "Question",
      "name": "Où se trouve L'Entracte ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "L'Entracte est situé au 3 rue de Versailles, 92410 Ville d'Avray, dans les Hauts-de-Seine, en Île-de-France, à 12 minutes de Paris.",
      },
    },
    {
      "@type": "Question",
      "name": "Y a-t-il une terrasse ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, L'Entracte dispose d'une magnifique terrasse fleurie de 240 m² surplombant un parc arboré, ouverte aux beaux jours.",
      },
    },
    {
      "@type": "Question",
      "name": "Peut-on offrir une carte cadeau L'Entracte ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui, nous proposons des cartes cadeaux dématérialisées de 25€ à 500€, envoyées par email en PDF, valables 1 an. Achetez-la directement sur la page /carte-cadeau.",
      },
    },
  ],
};
